import { Form, Formik, useField, useFormikContext } from "formik";
import * as Yup from "yup";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Client } from "@/features/clients/types";
import {
  DESIGNATION_OPTIONS,
  type FormSubmission,
  type GenerateFor,
  type PractitionerDesignation,
} from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCell, FormikCheckbox, FormikSelect, FormikTextarea } from "./formik-controls";

// --- Form RI-2848 editable payload ------------------------------------------
// snake_case keys mirror `apps/forms/mappings/form_2848ri.py` `build_defaults`
// exactly (the submission's `data` blob is passed through verbatim). RI diverges
// from the federal 2848: at most 2 reps, a single freeform tax-matters textarea,
// negative-authorization checkboxes (they *withhold* acts), and per-rep standing
// flags + a Part II declaration block, plus witness/notary attestation.

export interface Form2848riRepresentative {
  /** The backing Practitioner id (contact details are looked up from it server-side). */
  practitioner: string;
  active: boolean;
  name: string;
  designation: PractitionerDesignation | "";
  other_id: string;
  other_type: string;
  /** RI attestation flags. */
  good_standing: boolean;
  cpa: boolean;
  lpa: boolean;
  ea: boolean;
  /** Part II declaration cells. */
  declaration_designation: PractitionerDesignation | "";
  declaration_jurisdiction: string;
  declaration_date: string;
}

export interface Form2848riData {
  generate_for: GenerateFor;
  representatives: Form2848riRepresentative[];
  /** Single freeform statement of tax matters (no grid). */
  tax_matters: string;
  // Negative-authorization checkboxes — checked *withholds* the act.
  not_receive_checks: boolean;
  not_execute_waivers: boolean;
  not_execute_consents: boolean;
  not_represent: boolean;
  not_other: boolean;
  not_other_desc: string;
  // Witness / notary attestation.
  is_witnessed: boolean;
  is_notarized: boolean;
  witness_date_1: string;
  witness_date_2: string;
  notary_date: string;
  // Taxpayer signature dates.
  taxpayer_signature_date_1: string;
  taxpayer_signature_date_2: string;
  signer: string;
  signer_title: string;
}

const GENERATE_FOR_OPTIONS: { value: GenerateFor; label: string }[] = [
  { value: "taxpayer", label: "Taxpayer" },
  { value: "spouse", label: "Spouse" },
  { value: "jointly", label: "Jointly" },
];

// The RI negative-authorization checkboxes: each *removes* that authority.
const NOT_GRANTED: { name: keyof Form2848riData; label: string }[] = [
  { name: "not_receive_checks", label: "Do NOT receive refund checks" },
  { name: "not_execute_waivers", label: "Do NOT execute waivers of restrictions on assessment" },
  { name: "not_execute_consents", label: "Do NOT execute consents to extend the assessment period" },
  { name: "not_represent", label: "Do NOT represent the taxpayer before the Division" },
  { name: "not_other", label: "Do NOT perform other acts (specify)" },
];

const STANDING_FLAGS: { name: keyof Form2848riRepresentative; label: string }[] = [
  { name: "good_standing", label: "In good standing" },
  { name: "cpa", label: "Duly qualified CPA" },
  { name: "lpa", label: "Licensed public accountant" },
  { name: "ea", label: "Enrolled agent (IRS)" },
];

const schema = Yup.object({
  generate_for: Yup.string().oneOf(["taxpayer", "spouse", "jointly"]).required(),
  tax_matters: Yup.string().max(100, "Too long"),
  not_other_desc: Yup.string().max(50, "Too long"),
});

function normalizeRep(
  rep: Partial<Form2848riRepresentative>,
): Form2848riRepresentative {
  return {
    practitioner: rep.practitioner ?? "",
    active: rep.active ?? false,
    name: rep.name ?? "",
    designation: rep.designation ?? "",
    other_id: rep.other_id ?? "",
    other_type: rep.other_type ?? "",
    good_standing: rep.good_standing ?? false,
    cpa: rep.cpa ?? false,
    lpa: rep.lpa ?? false,
    ea: rep.ea ?? false,
    declaration_designation: rep.declaration_designation ?? rep.designation ?? "",
    declaration_jurisdiction: rep.declaration_jurisdiction ?? rep.other_type ?? "",
    declaration_date: rep.declaration_date ?? "",
  };
}

function toInitialValues(data: Record<string, unknown>): Form2848riData {
  const d = data as Partial<Form2848riData>;
  return {
    generate_for: d.generate_for ?? "taxpayer",
    representatives: (d.representatives ?? []).map(normalizeRep),
    tax_matters: d.tax_matters ?? "",
    not_receive_checks: d.not_receive_checks ?? false,
    not_execute_waivers: d.not_execute_waivers ?? false,
    not_execute_consents: d.not_execute_consents ?? false,
    not_represent: d.not_represent ?? false,
    not_other: d.not_other ?? false,
    not_other_desc: d.not_other_desc ?? "",
    is_witnessed: d.is_witnessed ?? false,
    is_notarized: d.is_notarized ?? false,
    witness_date_1: d.witness_date_1 ?? "",
    witness_date_2: d.witness_date_2 ?? "",
    notary_date: d.notary_date ?? "",
    taxpayer_signature_date_1: d.taxpayer_signature_date_1 ?? "",
    taxpayer_signature_date_2: d.taxpayer_signature_date_2 ?? "",
    signer: d.signer ?? "",
    signer_title: d.signer_title ?? "",
  };
}

function GenerateForField() {
  const [field, , helpers] = useField<GenerateFor>("generate_for");
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as GenerateFor)}
    >
      {GENERATE_FOR_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`gf-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`gf-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

/**
 * RI reps table — the client's assigned practitioners, but only the first two
 * *active* reps print on the form. Per rep: RI standing flags + a Part II
 * declaration (designation + jurisdiction). Contact details are filled from the
 * practitioner record server-side.
 */
function RiRepresentativesTable() {
  const { values } = useFormikContext<Form2848riData>();
  const reps = values.representatives;

  if (reps.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No representatives are assigned to this client. Add them on the client's
        profile, then reopen this form.
      </p>
    );
  }

  return (
    <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">Active</TableHead>
            <TableHead>Rep Name</TableHead>
            <TableHead className="w-44">Designation</TableHead>
            <TableHead className="w-44">Jurisdiction</TableHead>
            <TableHead>Standing</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reps.map((rep, i) => (
            <TableRow key={rep.practitioner || i}>
              <TableCell>
                <FormikCheckbox
                  name={`representatives.${i}.active`}
                  aria-label={`Active ${rep.name}`}
                />
              </TableCell>
              <TableCell className="font-medium">{rep.name}</TableCell>
              <TableCell>
                <FormikSelect
                  name={`representatives.${i}.declaration_designation`}
                  options={DESIGNATION_OPTIONS}
                  placeholder="Select…"
                  aria-label={`Designation for ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <FormikCell
                  name={`representatives.${i}.declaration_jurisdiction`}
                  aria-label={`Jurisdiction for ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <div className="flex flex-col gap-1">
                  {STANDING_FLAGS.map((flag) => (
                    <FormikCheckbox
                      key={flag.name}
                      name={`representatives.${i}.${flag.name}`}
                      label={flag.label}
                    />
                  ))}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function TaxMattersField() {
  return (
    <div className="grid gap-2">
      <Label htmlFor="tax_matters">Tax matters (describe the matters covered)</Label>
      <FormikTextarea name="tax_matters" id="tax_matters" rows={3} />
    </div>
  );
}

function NotOtherDescField() {
  return (
    <div className="grid gap-2">
      <Label htmlFor="not_other_desc">Other acts not authorized (specify)</Label>
      <FormikTextarea name="not_other_desc" id="not_other_desc" rows={2} />
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Form RI-2848 (Rhode Island Power of Attorney) — prefilled from the
 * client + its practitioners. RI-specific: a single freeform tax-matters
 * statement, negative-authorization checkboxes (each *withholds* an act), per-rep
 * standing flags + a Part II declaration, and a witness/notary attestation block.
 */
export function Form2848riEditor({ client, submission, onSubmissionChange }: Props) {
  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Formik
      initialValues={toInitialValues(submission.data)}
      validationSchema={schema}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <Card className="gap-6 p-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-foreground font-semibold">Generate for</span>
              <GenerateForField />
            </div>
            <div className="text-muted-foreground text-sm">
              <p className="text-foreground font-medium">{client.name}</p>
              {address && <p>{address}</p>}
            </div>
          </div>
        </Card>

        <section className="flex flex-col gap-4">
          <SectionHeading>Power of Attorney Given To</SectionHeading>
          <RiRepresentativesTable />
        </section>

        <Card className="gap-4 p-6">
          <SectionHeading>Tax Matters</SectionHeading>
          <TaxMattersField />
        </Card>

        <Card className="gap-4 p-6">
          <SectionHeading>Acts Not Authorized</SectionHeading>
          <p className="text-muted-foreground text-sm">
            Check each act you are <span className="font-medium">withholding</span> from
            the representative(s).
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {NOT_GRANTED.map((act) => (
              <FormikCheckbox key={act.name} name={act.name} label={act.label} />
            ))}
          </div>
          <NotOtherDescField />
        </Card>

        <Card className="gap-4 p-6">
          <SectionHeading>Witness / Notary</SectionHeading>
          <div className="grid gap-3 sm:grid-cols-2">
            <FormikCheckbox name="is_witnessed" label="This power of attorney is witnessed" />
            <FormikCheckbox name="is_notarized" label="This power of attorney is notarized" />
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="grid gap-2">
              <Label htmlFor="witness_date_1">Witness date 1</Label>
              <FormikCell name="witness_date_1" type="date" aria-label="Witness date 1" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="witness_date_2">Witness date 2</Label>
              <FormikCell name="witness_date_2" type="date" aria-label="Witness date 2" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notary_date">Notary date</Label>
              <FormikCell name="notary_date" type="date" aria-label="Notary date" />
            </div>
          </div>
        </Card>

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
